import React, { useEffect, useMemo, useState } from 'react';
import { Modal, ModalContent, ModalHeader, ModalTitle, ModalDescription, ModalBody, ModalFooter, useToast } from '../../sectepe-ui/index';
import leadSyncService, { BulkImportResult, CreateLeadInput } from '../../services/leadSyncService';
import { StatusBadge } from '../../components/ui';
import { Icon } from '../../components/icons';
import { fehlerText } from '../format';
import { parseLeadCsv } from './leadCsv';

interface Props {
  open: boolean;
  onClose: () => void;
  onImported: (ergebnis: BulkImportResult) => void;
}

type Modus = 'csv' | 'json';

const BEISPIEL_CSV = `email;vorname;nachname;firma;position
max@beispiel.de;Max;Mustermann;Beispiel GmbH;CISO
anna@firma.de;Anna;Schmidt;Firma AG;IT-Leiterin`;

const BEISPIEL_JSON = `[
  { "email": "max@beispiel.de", "firstName": "Max", "lastName": "Mustermann", "company": "Beispiel GmbH" },
  { "email": "anna@firma.de", "firstName": "Anna", "company": "Firma AG" }
]`;

const VORSCHAU_SPALTEN: { feld: keyof CreateLeadInput; label: string }[] = [
  { feld: 'email', label: 'E-Mail' }, { feld: 'firstName', label: 'Vorname' }, { feld: 'lastName', label: 'Nachname' },
  { feld: 'company', label: 'Firma' }, { feld: 'jobTitle', label: 'Position' }, { feld: 'location', label: 'Ort' },
];

/** JSON-Text pruefen; liefert Leads oder eine Fehlermeldung. */
const parseJson = (text: string): { leads: CreateLeadInput[]; fehler?: string } => {
  if (!text.trim()) return { leads: [] };
  try {
    const wert = JSON.parse(text);
    if (!Array.isArray(wert)) return { leads: [], fehler: 'Die Eingabe muss ein JSON-Array sein.' };
    const ohne = wert.filter((l) => !l || typeof l.email !== 'string' || !l.email.trim()).length;
    if (ohne) return { leads: [], fehler: `${ohne} Einträge haben keine E-Mail-Adresse.` };
    return { leads: wert as CreateLeadInput[] };
  } catch {
    return { leads: [], fehler: 'Kein gültiges JSON. Erwartet wird ein Array von Objekten mit mindestens „email“.' };
  }
};

/**
 * Import aus CSV (Excel, Sheets, LinkedIn-Export) oder JSON. Die Vorschau
 * zeigt, wie die Spalten gelesen wurden, bevor etwas gespeichert wird.
 */
export const ImportDialog: React.FC<Props> = ({ open, onClose, onImported }) => {
  const { push } = useToast();
  const [modus, setModus] = useState<Modus>('csv');
  const [text, setText] = useState('');
  const [dateiName, setDateiName] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [ergebnis, setErgebnis] = useState<BulkImportResult | null>(null);

  useEffect(() => {
    if (open) {
      setText('');
      setDateiName(null);
      setFehler(null);
      setErgebnis(null);
    }
  }, [open]);

  const csv = useMemo(() => (modus === 'csv' ? parseLeadCsv(text) : null), [modus, text]);
  const json = useMemo(() => (modus === 'json' ? parseJson(text) : null), [modus, text]);
  const leads = csv?.leads ?? json?.leads ?? [];
  const eingabeFehler = json?.fehler ?? (csv && csv.zeilen > 0 && csv.leads.length === 0 ? 'Keine Zeile mit gültiger E-Mail-Adresse gefunden. Enthält die Kopfzeile eine Spalte „email“ oder „E-Mail“?' : null);

  const dateiLesen = (datei: File | undefined) => {
    if (!datei) return;
    const leser = new FileReader();
    leser.onload = () => {
      setText(String(leser.result ?? ''));
      setDateiName(datei.name);
    };
    leser.readAsText(datei);
  };

  const importieren = async () => {
    if (!leads.length) return;
    setLaeuft(true);
    setFehler(null);
    try {
      const b = await leadSyncService.bulkImportLeads(leads);
      setErgebnis(b);
      push({
        message: `${b.created} Leads importiert${b.skipped ? `, ${b.skipped} übersprungen` : ''}${b.errors.length ? `, ${b.errors.length} Fehler` : ''}.`,
        severity: b.errors.length ? 'warning' : 'success',
        duration: 8000,
      });
      onImported(b);
    } catch (err) {
      setFehler(fehlerText(err, 'Import fehlgeschlagen'));
    } finally {
      setLaeuft(false);
    }
  };

  return (
    <Modal open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <ModalContent className="detail-modal">
        <ModalHeader>
          <ModalTitle>Leads importieren</ModalTitle>
          <ModalDescription>
            CSV aus Excel, Google Sheets oder einem LinkedIn-Export einfügen oder als Datei wählen. Vorhandene E-Mail-Adressen werden übersprungen.
          </ModalDescription>
        </ModalHeader>
        <ModalBody>
          <div className="tabs import-modus" role="tablist" aria-label="Format">
            {(['csv', 'json'] as Modus[]).map((m) => (
              <button key={m} type="button" role="tab" aria-selected={modus === m} className={`tab ${modus === m ? 'tab-active' : ''}`} onClick={() => { setModus(m); setErgebnis(null); }}>
                {m === 'csv' ? 'CSV' : 'JSON'}
              </button>
            ))}
          </div>

          {fehler && <div className="alert alert-error">{fehler}</div>}

          {!ergebnis && (
            <>
              {modus === 'csv' && (
                <label className="import-datei btn btn-secondary btn-sm">
                  <Icon name="archive" size={14} /> {dateiName ? `Datei: ${dateiName}` : 'CSV-Datei wählen'}
                  <input type="file" accept=".csv,.txt,text/csv" onChange={(e) => dateiLesen(e.target.files?.[0])} />
                </label>
              )}
              <textarea
                className="form-textarea import-text"
                rows={8}
                aria-label={modus === 'csv' ? 'CSV-Text' : 'JSON-Text'}
                placeholder={modus === 'csv' ? BEISPIEL_CSV : BEISPIEL_JSON}
                value={text}
                onChange={(e) => { setText(e.target.value); setDateiName(null); }}
              />
              {modus === 'csv' && (
                <p className="text-xs text-secondary">
                  Erkannte Spalten: E-Mail, Vorname, Nachname, Firma, Position, Telefon, LinkedIn, Website, Ort, Notizen, Quelle – auf Deutsch oder Englisch. Trennzeichen (; , Tab) wird erkannt.
                </p>
              )}
              {eingabeFehler && text.trim() && <div className="alert alert-warning">{eingabeFehler}</div>}

              {leads.length > 0 && (
                <div className="import-vorschau" aria-label="Vorschau">
                  <div className="import-vorschau__kopf">
                    <StatusBadge tone="success" icon="check">{leads.length} Leads bereit</StatusBadge>
                    {csv && csv.ohneEmail > 0 && <StatusBadge tone="warning">{csv.ohneEmail} Zeilen ohne gültige E-Mail</StatusBadge>}
                    {csv && csv.unbekannt.length > 0 && <span className="text-xs text-secondary">nicht zugeordnet: {csv.unbekannt.join(', ')}</span>}
                  </div>
                  <div className="table-container">
                    <table className="table">
                      <thead><tr>{VORSCHAU_SPALTEN.map((s) => <th key={s.feld}>{s.label}</th>)}</tr></thead>
                      <tbody>
                        {leads.slice(0, 5).map((l, i) => (
                          <tr key={`${l.email}-${i}`}>{VORSCHAU_SPALTEN.map((s) => <td key={s.feld} className="text-sm">{(l[s.feld] as string) || '—'}</td>)}</tr>
                        ))}
                        {leads.length > 5 && <tr><td colSpan={VORSCHAU_SPALTEN.length} className="text-xs text-secondary">… und {leads.length - 5} weitere</td></tr>}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}

          {ergebnis && (
            <div className="import-vorschau" aria-label="Import-Ergebnis">
              <div className="import-vorschau__kopf">
                <StatusBadge tone="success" icon="check">{ergebnis.created} importiert</StatusBadge>
                {ergebnis.skipped > 0 && <StatusBadge tone="neutral">{ergebnis.skipped} übersprungen (schon vorhanden)</StatusBadge>}
                {ergebnis.errors.length > 0 && <StatusBadge tone="danger">{ergebnis.errors.length} Fehler</StatusBadge>}
              </div>
              {ergebnis.errors.length > 0 && (
                <ul className="notiz-liste">
                  {ergebnis.errors.map((e) => <li key={e.email}><strong>{e.email}</strong> – {e.error}</li>)}
                </ul>
              )}
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          {ergebnis ? (
            <button type="button" className="btn btn-primary" onClick={onClose}>Fertig</button>
          ) : (
            <>
              <button type="button" className="btn btn-ghost" onClick={onClose}>Abbrechen</button>
              <button type="button" className="btn btn-primary" onClick={importieren} disabled={laeuft || leads.length === 0}>
                {laeuft ? 'Importiert…' : `${leads.length || ''} Leads importieren`.trim()}
              </button>
            </>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
